<template>
    <zen-slide-in
        :isOpen="isOpen"
        title="Create flag?"
        :error="error"
        @close="$emit('close')"
    >
        <template #body>
            <div class="space-y-5">
                <div>
                    <label
                        for="project-name"
                        class="block text-sm lg:text-base font-medium text-taupe-800"
                        >Flag name</label
                    >
                    <div class="mt-1">
                        <input
                            type="text"
                            @keyup="flagNameToSlug"
                            name="project-name"
                            id="project-name"
                            :value="data.name"
                            @input="
                                $emit('update:data', {
                                    ...data,
                                    name: $event.target.value,
                                })
                            "
                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-taupe-500 focus:ring-taupe-500 sm:text-sm lg:text-base"
                        />
                    </div>
                </div>
                <div>
                    <label
                        for="project-key"
                        class="block text-sm lg:text-base font-medium text-taupe-800"
                        >Key</label
                    >
                    <div class="mt-1">
                        <input
                            type="text"
                            @keyup="checkFlagSlug"
                            name="project-key"
                            id="project-key"
                            :value="data.key"
                            @input="
                                $emit('update:data', {
                                    ...data,
                                    key: $event.target.value,
                                })
                            "
                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-taupe-500 focus:ring-taupe-500 sm:text-sm lg:text-base font-mono"
                        />
                    </div>
                </div>
                <div>
                    <label
                        for="description"
                        class="block text-sm lg:text-base font-medium text-taupe-800"
                        >Description</label
                    >
                    <div class="mt-1">
                        <textarea
                            id="description"
                            name="description"
                            :value="data.description"
                            @input="
                                $emit('update:data', {
                                    ...data,
                                    description: $event.target.value,
                                })
                            "
                            rows="4"
                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-taupe-500 focus:ring-taupe-500 sm:text-sm lg:text-base"
                        ></textarea>
                    </div>
                </div>
                <div>
                    <label
                        id="project-type-label"
                        for="project-type"
                        class="block text-sm lg:text-base font-medium text-taupe-800"
                        >Type</label
                    >
                    <div v-if="!showAlternateTypeSelector" class="mt-1">
                        <select
                            name="project-type"
                            id="project-type"
                            :value="data.type"
                            @change="
                                $emit('update:data', {
                                    ...data,
                                    type: $event.target.value,
                                })
                            "
                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-taupe-500 focus:ring-taupe-500 sm:text-sm lg:text-base"
                        >
                            <option
                                v-for="option in typeOptions"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.value }}
                            </option>
                        </select>
                    </div>

                    <div
                        v-if="showAlternateTypeSelector"
                        ref="typeOptionGroup"
                        class="flex space-x-3 mt-2"
                        role="radiogroup"
                        aria-labelledby="project-type-label"
                        @keydown="handleTypeOptionKeydown"
                    >
                        <button
                            v-for="option in typeOptions"
                            :key="option.value"
                            type="button"
                            role="radio"
                            :aria-checked="data.type === option.value"
                            :tabindex="data.type === option.value ? 0 : -1"
                            @click="
                                $emit('update:data', {
                                    ...data,
                                    type: option.value,
                                })
                            "
                            :class="
                                data.type === option.value
                                    ? 'border-sky-500 text-sky-500'
                                    : 'border-taupe-300 text-taupe-500'
                            "
                            class="relative min-w-0 flex-1 border border-2 rounded-md text-center p-3 cursor-pointer bg-transparent"
                        >
                            <zen-icon-box-checked
                                v-if="data.type === option.value"
                                aria-hidden="true"
                                css-classes="h-4 w-4 absolute top-1.5 right-1.5"
                            />
                            <div class="inline-block">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="h-10 w-10"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    aria-hidden="true"
                                >
                                    <path
                                        stroke="none"
                                        d="M0 0h24v24H0z"
                                        fill="none"
                                    />
                                    <path
                                        v-for="(d, index) in option.iconPaths"
                                        :key="index"
                                        :d="d"
                                    />
                                </svg>
                            </div>
                            <div class="uppercase text-sm">
                                {{ option.value }}
                            </div>
                        </button>
                    </div>
                </div>
                <div data-testid="create-flag-platform-inclusion-section">
                    <label
                        class="block text-sm lg:text-base font-medium text-taupe-800"
                        >Platform Inclusion</label
                    >
                    <div
                        class="mt-2 space-y-3 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3"
                    >
                        <label
                            for="create-flag-include-in-mobile"
                            class="flex items-start gap-3"
                        >
                            <input
                                id="create-flag-include-in-mobile"
                                name="include_in_mobile"
                                type="checkbox"
                                data-testid="create-flag-mobile-inclusion-checkbox"
                                :checked="data.include_in_mobile"
                                @change="
                                    $emit('update:data', {
                                        ...data,
                                        include_in_mobile:
                                            $event.target.checked,
                                    })
                                "
                                class="mt-0.5 h-4 w-4 rounded border-taupe-300"
                            />
                            <span class="text-sm text-taupe-800"
                                >Include this flag in mobile payloads.</span
                            >
                        </label>
                        <label
                            for="create-flag-include-in-client"
                            class="flex items-start gap-3"
                        >
                            <input
                                id="create-flag-include-in-client"
                                name="include_in_client"
                                type="checkbox"
                                data-testid="create-flag-client-inclusion-checkbox"
                                :checked="data.include_in_client"
                                @change="
                                    $emit('update:data', {
                                        ...data,
                                        include_in_client:
                                            $event.target.checked,
                                    })
                                "
                                class="mt-0.5 h-4 w-4 rounded border-taupe-300"
                            />
                            <span class="text-sm text-taupe-800"
                                >Include this flag in client payloads.</span
                            >
                        </label>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:buttons>
            <zen-button variant="secondary" @click="$emit('close')">
                Cancel
            </zen-button>
            <zen-button type="submit" @click="$emit('confirm')">
                Save
            </zen-button>
        </template>
    </zen-slide-in>
</template>

<script>
export default {
    name: 'CreateFlagSlideIn',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
        error: {
            type: [String, Object],
            default: null,
        },
        showAlternateTypeSelector: {
            type: Boolean,
            default: false,
        },
        typeOptions: {
            type: Array,
            required: true,
        },
        flagNameToSlug: {
            type: Function,
            required: true,
        },
        checkFlagSlug: {
            type: Function,
            required: true,
        },
    },
    emits: ['close', 'confirm', 'update:data'],
    methods: {
        handleTypeOptionKeydown(event) {
            const directions = new Map([
                ['ArrowRight', 1],
                ['ArrowDown', 1],
                ['ArrowLeft', -1],
                ['ArrowUp', -1],
            ])

            const step = directions.get(event.key)
            if (!step) {
                return
            }

            event.preventDefault()

            const options = this.typeOptions
            const currentIndex = options.findIndex(
                (option) => option.value === this.data.type
            )
            const nextIndex =
                (currentIndex + step + options.length) % options.length
            const nextOption = options.at(nextIndex)

            this.$emit('update:data', { ...this.data, type: nextOption.value })

            this.$nextTick(() => {
                const group = this.$refs.typeOptionGroup
                const nextButton = group
                    ?.querySelectorAll('[role="radio"]')
                    .item(nextIndex)
                nextButton?.focus()
            })
        },
    },
}
</script>
